/** Food diary for the selected day: your sections in your order, the watch block, totals on the right. */
import { useMemo, useState, type ReactElement } from 'react';
import { DRI_CITATIONS, driTargets, targetStatus, type DriResult } from '@engine/dri';
import { NUTRIENT_BY_KEY } from '@engine/nutrients';
import { ActivityPanel } from '../components/ActivityPanel';
import { AddFoodModal } from '../components/AddFoodModal';
import { DiaryLayoutModal } from '../components/DiaryLayoutModal';
import { Icon } from '../components/Icon';
import { MealDetailModal } from '../components/MealDetailModal';
import { NutrientPanel } from '../components/NutrientPanel';
import { EnergySummary, TargetsPanel } from '../components/Panels';
import { QuickAdd } from '../components/QuickAdd';
import { Card, Empty, Skeleton } from '../components/ui';
import { ageOn, formatTime } from '../lib/dates';
import { effectiveLayout, groupMeals, OTHER_GROUP_ID, type DiaryGroup } from '../lib/diaryLayout';
import { dayNutrients } from '../lib/foodNutrients';
import { SOURCE_LABEL } from '../lib/labels';
import { sumNutrients } from '../lib/nutrition';
import type { DiaryBlock, MealLog } from '../lib/types';
import { useApp, useAsync, useDayBurn } from '../state/app';

const g = (x: number): string => `${Math.round(x)} g`;

/** "420 kcal · 30 g protein · 40 g carbs · 12 g fat" for a list of meals. */
export function MacroWords({ calories, protein, carbs, fat }: { calories: number; protein: number; carbs: number; fat: number }): ReactElement {
  return (
    <span className="macro-words num">
      <span>{Math.round(calories).toLocaleString()} kcal</span>
      <span className="p">{g(protein)} protein</span>
      <span className="c">{g(carbs)} carbs</span>
      <span className="f">{g(fat)} fat</span>
    </span>
  );
}

export function DiaryPage(): ReactElement {
  const { source, profile, settings, settingsStored, day, version, bump, energy } = useApp();
  const { data: meals, error, loading } = useAsync<MealLog[]>(() => source.listMeals(day), [source, day, version]);
  const [adding, setAdding] = useState<DiaryBlock | null>(null);
  const [viewing, setViewing] = useState<MealLog | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [customizing, setCustomizing] = useState<boolean>(false);
  const list: MealLog[] = useMemo(() => meals ?? [], [meals]);
  const totals = sumNutrients(list);
  const dayValues = dayNutrients(list);
  const { burn } = useDayBurn(day, list);
  const layout: DiaryBlock[] = effectiveLayout(settings.diary_layout, settings.energy_mode);
  const groups: DiaryGroup[] = groupMeals(layout, list);
  const dayRows = energy.activityRows.filter((r) => r.local_date === day);
  const energyTarget: number | null = profile.target_calories ?? energy.planning.kcal;
  const dri: DriResult = useMemo(() => driTargets({
    sex: profile.sex,
    ageYears: profile.birth_date ? ageOn(profile.birth_date, day) : null,
    weightKg: energy.comp.weightKg,
    energyKcal: energyTarget,
  }), [profile.sex, profile.birth_date, day, energy.comp.weightKg, energyTarget]);
  // The micronutrients furthest from target today (only ones with data), for a quick "eat more of" hint.
  const lowest = [...dri.targets.values()]
    // Fluoride mostly comes from drinking water, which isn't logged, so it's left out of the hint.
    .filter((t) => !t.isLimit && t.key !== 'fluoride_ug' && dayValues[t.key] !== undefined && targetStatus(dayValues[t.key], t) === 'low')
    .map((t) => ({ t, pct: (dayValues[t.key] as number) / t.amount }))
    .sort((a, b) => a.pct - b.pct).slice(0, 4);
  const overs = [...dri.targets.values()].filter((t) => { const st = targetStatus(dayValues[t.key], t); return st === 'over_limit' || st === 'over_ul'; });

  const toggle = (id: string): void => setExpanded((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const foodGroup = ({ block, meals: rows }: DiaryGroup): ReactElement => {
    const t = sumNutrients(rows);
    return (
      <div key={block.id} className="diary-group">
        <div className="dg-head">
          <h3>{block.label}</h3>
          {rows.length > 0 && <span className="sum"><MacroWords calories={t.calories} protein={t.protein_g} carbs={t.carbs_g} fat={t.fat_g} /></span>}
          {block.id !== OTHER_GROUP_ID && <div className="right"><button type="button" className="btn btn-sm btn-ghost" onClick={() => setAdding(block)}><Icon name="plus" size={16} /> Add</button></div>}
        </div>
        {rows.map((m) => (
          <div key={m.id}>
            <div className="entry" onClick={() => setViewing(m)} onDoubleClick={() => setViewing(m)} role="button" tabIndex={0}
              title="Click for every macro and micronutrient" onKeyDown={(e) => { if (e.key === 'Enter') setViewing(m); }}>
              <div style={{ minWidth: 0 }}>
                <div className="desc">{m.description}</div>
                <div className="meta">
                  <span>{formatTime(m.eaten_at)}</span><span>{SOURCE_LABEL[m.source]}</span>
                  {m.items.length > 0 && <button type="button" className="link-btn" style={{ fontSize: 12, color: 'var(--text-3)' }} onClick={(e) => { e.stopPropagation(); toggle(m.id); }}>{expanded.has(m.id) ? 'hide' : `${m.items.length} item${m.items.length === 1 ? '' : 's'}`}</button>}
                </div>
              </div>
              <div className="macro-chips">
                <span className="chip p num">{g(m.protein_g)} protein</span>
                <span className="chip c num">{g(m.carbs_g)} carbs</span>
                <span className="chip f num">{g(m.fat_g)} fat</span>
              </div>
              <div className="kcal num">{Math.round(m.calories)}<small>kcal</small></div>
            </div>
            {expanded.has(m.id) && (
              <div className="item-list">
                {m.items.map((it, i) => <div key={i}><span>{it.quantity} {it.unit} {it.name}</span><span className="num">{Math.round(it.calories)} kcal</span></div>)}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="grid cols-main">
      <div className="stack">
        <QuickAdd />
        <Card title="Food diary" sub={`${list.length} ${list.length === 1 ? 'entry' : 'entries'}`}
          right={<button type="button" className="btn btn-sm" onClick={() => setCustomizing(true)}><Icon name="sliders" size={15} /> Customize</button>}>
          {error !== null && <div className="note err">{error}</div>}
          {settingsStored === 'device' && layout.some((b) => b.kind === 'food' && !['breakfast', 'lunch', 'dinner', 'snack', 'pre_workout', 'post_workout', 'other'].includes(b.id)) && (
            <div className="note warn" style={{ marginBottom: 8 }}>Custom sections are kept in this browser until the database update runs; food logged in them is saved under Other until then.</div>
          )}
          {loading && meals === null ? <Skeleton height={260} /> : groups.map((grp) => (
            grp.block.kind === 'activity'
              ? (
                <div key={grp.block.id} className="diary-group">
                  <div className="dg-head"><h3>{grp.block.label}</h3></div>
                  <ActivityPanel day={day} rows={dayRows} fields={grp.block.fields} burn={burn} units={profile.unit_system} />
                </div>
              )
              : foodGroup(grp)
          ))}
          {!loading && list.length === 0 && error === null && <Empty title="Nothing logged yet">Tap <b>Food</b> or <b>Photo</b> above, or <b>Add</b> next to a section.</Empty>}
        </Card>
      </div>
      <div className="stack">
        <EnergySummary totals={totals} profile={profile} burn={burn} />
        <TargetsPanel totals={totals} profile={profile} />
        <Card title="Nutrients" sub={dri.targets.size > 0 ? 'today vs your daily targets' : 'today, all 94'}>
          {list.length > 0 && dri.targets.size > 0 && (lowest.length > 0 || overs.length > 0) && (
            <div className="note" style={{ marginBottom: 8, fontSize: 13 }}>
              {lowest.length > 0 && <div><b>Lowest today:</b> {lowest.map(({ t, pct }) => `${NUTRIENT_BY_KEY.get(t.key)?.name ?? t.key} ${Math.round(pct * 100)}%`).join(' · ')}</div>}
              {overs.length > 0 && <div className="warn"><b>Over:</b> {overs.map((t) => NUTRIENT_BY_KEY.get(t.key)?.name ?? t.key).join(' · ')}</div>}
            </div>
          )}
          {dri.notes.map((n) => <div key={n} className="faint" style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>{n}</div>)}
          <NutrientPanel values={dayValues} targets={dri.targets.size > 0 ? dri.targets : undefined} showSources={false} defaultOpen={['general', 'carbohydrates', 'lipids']} />
          <p className="cite" style={{ margin: '8px 0 0' }}>“—” means no logged food reported that nutrient. Foods from the database carry up to 94 values; quick AI estimates only carry the basics.</p>
          {dri.targets.size > 0 && (
            <details style={{ marginTop: 6 }}>
              <summary className="cite" style={{ cursor: 'pointer', fontWeight: 700 }}>Where the targets come from</summary>
              <p className="cite" style={{ margin: '6px 0' }}>
                Targets are the Dietary Reference Intakes for your age and sex: the RDA (enough for about 97% of healthy people) or, where there isn’t enough data for one, the Adequate Intake. Sodium, saturated fat and added sugars are limits to stay under. Bars turn red only above a safe upper limit that applies to food; hover a bar for details. A low % on one day is normal. What matters is the average over a week.
              </p>
              <ol className="cite" style={{ paddingLeft: 18, margin: 0 }}>{DRI_CITATIONS.map((c) => <li key={c.key}>{c.text} <a href={c.url} target="_blank" rel="noreferrer">link</a></li>)}</ol>
            </details>
          )}
        </Card>
      </div>
      {adding !== null && <AddFoodModal day={day} defaultSectionId={adding.id} onClose={() => setAdding(null)} onSaved={bump} />}
      {viewing !== null && <MealDetailModal meal={viewing} onClose={() => setViewing(null)} onChanged={bump} targets={dri.targets} />}
      {customizing && <DiaryLayoutModal onClose={() => setCustomizing(false)} />}
    </div>
  );
}
